import React, { FC, useEffect } from 'react'
import { Link, Route, Routes, useParams } from 'react-router-dom'
import SlotOutlineComponent from '@components/slot/slotOutline.component'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  fetchPlaylistById,
  selectIsPlaylistIdInProgress,
  selectPlaylistById,
} from '@store/slices/playlist.slice'
import {
  AISlotModel,
  fetchAllAiSlots,
  selectAiSlotById,
  selectAiSlotLoading,
  selectAiSlotsByIds,
} from '@store/slices/aiSlot.slice'
import { Col, ListGroup } from 'react-bootstrap'
import { DjangoFilter } from '@store/slices/generic.slice'
import {
  selectAIMetadataById,
  selectAIMetadataByIds,
  selectAIMetadataLoading,
} from '@store/slices/aiMeta.slice'
import { toHourMinString } from '@shared/tools/time.tools'
import { EntityId } from '@reduxjs/toolkit'
import { fetchAllMetadataBySlotIds } from '@store/thunk/aimeta.thunk'

const PlaylistOutlineComponent: FC = ({}) => {
  return (
    <div className={'d-flex gap-2'}>
      <Col className={'w-50'}>
        <PlaylistOutline></PlaylistOutline>
      </Col>
      <Col className={'w-50'}>
        <Routes>
          <Route path=":slotId" element={<SlotOutlineComponent />} />
        </Routes>
      </Col>
    </div>
  )
}

const PlaylistOutline: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { playlistId } = useParams()
  const playlist = useSelector((state: RootState) =>
    playlistId ? selectPlaylistById(state, playlistId) : null
  )
  const playlistLoading = useSelector((state: RootState) =>
    playlistId ? selectIsPlaylistIdInProgress(state, playlistId) : null
  )

  const aiSlotsLoading = useSelector(selectAiSlotLoading)

  const aiSlots: AISlotModel[] = useSelector((state: RootState) =>
    playlist ? selectAiSlotsByIds(state, playlist.aiSlots) : []
  )

  const playlistMetadataIds = aiSlots.flatMap((aiSlot) =>
    aiSlot.aiEntryMetadata ? [aiSlot.aiEntryMetadata] : []
  )
  const playlistMetadata = useSelector((state: RootState) =>
    selectAIMetadataByIds(state, playlistMetadataIds)
  )

  const metadataLoading = useSelector(selectAIMetadataLoading)

  useEffect(() => {
    if (playlistId && !playlist && !playlistLoading)
      dispatch(fetchPlaylistById(playlistId))
  }, [playlistId])

  useEffect(() => {
    if (
      playlist &&
      !aiSlotsLoading &&
      aiSlots.length !== playlist.aiSlots.length
    )
      fetchSlotsForPlaylist()
  }, [playlist])

  useEffect(() => {
    if (
      playlistMetadataIds.length > 0 &&
      !metadataLoading &&
      playlistMetadata.length != playlistMetadataIds.length
    )
      playlist && dispatch(fetchAllMetadataBySlotIds(playlist.aiSlots))
  }, [playlistMetadataIds])

  const fetchSlotsForPlaylist = () => {
    const filter = new DjangoFilter<AISlotModel>()
    playlist && filter.addIdFilter(playlist.aiSlots)
    dispatch(fetchAllAiSlots(filter))
  }

  const OutlineTable: FC = () => (
    <ListGroup>
      <ListGroup.Item active>
        <div className="d-flex justify-content-between">
          <span>#</span>
          <span>plid</span>
          <span>Čas</span>
          <span>Typ</span>
          <span>Reg</span>
        </div>
      </ListGroup.Item>
      <div style={{ maxHeight: '700px' }} className="overflow-y-scroll">
        {aiSlots
          ?.sort(
            (a, b) =>
              new Date(a.exactTime).getTime() - new Date(b.exactTime).getTime()
          )
          .map((aiSlot, index) => (
            <SlotRowComponent
              aiSlotId={aiSlot.id}
              index={index}
              key={index}
            ></SlotRowComponent>
          ))}
      </div>
    </ListGroup>
  )

  return (
    <div className="my-2">
      <OutlineTable></OutlineTable>
    </div>
  )
}

const SlotRowComponent: FC<{ aiSlotId: EntityId; index: number }> = ({
  aiSlotId,
  index,
}) => {
  const { playlistId } = useParams()
  const aiSlot: AISlotModel | undefined = useSelector((state: RootState) =>
    selectAiSlotById(state, aiSlotId)
  )
  const aiMetadata = useSelector((state: RootState) =>
    aiSlot?.aiEntryMetadata
      ? selectAIMetadataById(state, aiSlot.aiEntryMetadata)
      : null
  )

  return (
    <ListGroup.Item
      as={Link}
      to={`/playlist/${playlistId}/outline/${aiSlot?.id}`}
      action
      key={index}
    >
      <div className="d-flex justify-content-between">
        <span>{index}</span>
        <span>{aiSlot?.plid}</span>
        <span>{aiSlot && toHourMinString(aiSlot.exactTime)}</span>
        <span style={{ minWidth: '60px' }}>
          {!!aiMetadata ? aiMetadata.aiLinkType : 'Není vygenerováno'}
        </span>
        <span>
          {!!aiMetadata ? (aiMetadata.regionalSignature ? '✓' : '✗') : '?'}
        </span>
      </div>
    </ListGroup.Item>
  )
}

export default PlaylistOutlineComponent
